import React, { useEffect, useState } from "react";
import { Seo } from "../components/Seo";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, MapPin, Loader2, CheckCircle2, Vote, Clock, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicEvent() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 0 });
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [claimingShift, setClaimingShift] = useState(null);
  const [shiftMsg, setShiftMsg] = useState(null);

  const loadShifts = () => axios.get(`${API}/api/public/event/${slug}/shifts`)
    .then(r => setShifts(r.data.shifts || []))
    .catch(() => setShifts([]));

  useEffect(() => {
    axios.get(`${API}/api/public/event/${slug}`)
      .then(r => setEv(r.data))
      .catch(e => setError(e.response?.data?.detail || "Event not found"));
    loadShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const rsvp = async () => {
    if (!form.name.trim() || !form.email.includes("@")) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/public/event/${slug}/rsvp`, form);
      setDone(data.message);
    } catch (e) { setError(e.response?.data?.detail || "Could not RSVP"); }
    finally { setSubmitting(false); }
  };

  const claimShift = async (sid) => {
    if (!form.name.trim() || !form.email.includes("@")) {
      setError("Add your name and email above first");
      return;
    }
    setClaimingShift(sid);
    try {
      const { data } = await axios.post(`${API}/api/public/event/${slug}/shifts/${sid}/claim`,
        { name: form.name, email: form.email, phone: form.phone });
      setShiftMsg(data.message || "Signed up!");
      loadShifts();
    } catch (e) { setError(e.response?.data?.detail || "Could not sign up"); }
    finally { setClaimingShift(null); }
  };

  return (
    <div className="min-h-screen bg-app text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="public-event-page">
        {ev && <Seo title={`${ev.name} — ${ev.entity_name || "campaign event"}`} description={(ev.description || `${ev.event_type || "Event"} on ${ev.date || "TBA"}${ev.location ? ` · ${ev.location}` : ""}`).slice(0, 160)} type="event"
          jsonLd={{ "@context": "https://schema.org", "@type": "Event", name: ev.name, startDate: ev.date ? `${ev.date}${ev.time ? `T${ev.time}` : ""}` : undefined, description: ev.description, location: ev.location ? { "@type": "Place", name: ev.location } : undefined, organizer: ev.entity_name ? { "@type": "Organization", name: ev.entity_name } : undefined, eventStatus: "https://schema.org/EventScheduled" }} />}
        {!ev && !error && <Loader2 className="w-8 h-8 animate-spin text-cyan-300 mx-auto" />}
        {error && !ev && <div className="text-center text-slate-400">{error}</div>}
        {ev && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30">
              <Vote className="w-3.5 h-3.5 text-cyan-300" />
              <span className="text-xs text-cyan-300 uppercase tracking-wider">{ev.entity_name}</span>
            </div>
            <h1 className="font-display text-3xl font-bold" data-testid="event-title">{ev.name}</h1>
            <div className="text-sm text-slate-400 space-y-1">
              <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-cyan-300/70" /> {ev.date}{ev.time ? ` · ${ev.time}` : ""}</div>
              {ev.location && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-cyan-300/70" /> {ev.location}</div>}
            </div>
            {ev.description && <p className="text-sm text-slate-300">{ev.description}</p>}
            {done ? (
              <div className="text-center py-4" data-testid="rsvp-confirmation">
                <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-2" />
                <p className="text-emerald-300 font-semibold">{done}</p>
              </div>
            ) : ev.full ? (
              <div className="text-amber-300 text-sm text-center py-2">This event is at capacity.</div>
            ) : (
              <div className="space-y-3 pt-2">
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="rsvp-name-input"
                  className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none" />
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" data-testid="rsvp-email-input"
                  className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none" />
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone (optional, for shift reminders)" data-testid="rsvp-phone-input"
                  className="w-full bg-slate-900/80 border border-white/15 rounded-xl px-4 py-3 text-sm focus:border-cyan-400/60 focus:outline-none" />
                {error && <p className="text-xs text-red-400">{error}</p>}
                <button onClick={rsvp} disabled={submitting} data-testid="rsvp-submit-btn"
                  className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-bold rounded-xl py-3 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                  {submitting ? "Saving…" : "RSVP — count me in"}
                </button>
                <p className="text-[10px] text-slate-600 text-center">{ev.rsvp_count} people are attending</p>
              </div>
            )}

            {/* Volunteer shifts */}
            {shifts.length > 0 && (
              <div className="pt-4 border-t border-white/10" data-testid="public-shifts">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-emerald-300" />
                  <div className="text-sm font-semibold text-white">Pick a volunteer shift</div>
                </div>
                <div className="text-[11px] text-slate-500 mb-3">Add your name + email above, then tap the shift you want.</div>
                {shiftMsg && (
                  <div className="mb-3 rounded-lg bg-emerald-400/10 border border-emerald-400/30 text-emerald-200 text-xs px-3 py-2" data-testid="shift-confirmation">{shiftMsg}</div>
                )}
                <div className="space-y-2">
                  {shifts.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => claimShift(s.id)}
                      disabled={s.remaining === 0 || claimingShift === s.id}
                      data-testid={`public-shift-${s.id}`}
                      className={`w-full text-left rounded-lg border px-3 py-2 transition ${s.remaining === 0
                        ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                        : "border-white/10 bg-white/[0.03] hover:border-emerald-400/40 hover:bg-emerald-400/[0.05]"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate">{s.title}</div>
                          <div className="text-[11px] text-slate-500">{(s.starts_at || "").slice(0, 16).replace("T", " ")} → {(s.ends_at || "").slice(11, 16)}</div>
                          {s.notes && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{s.notes}</div>}
                        </div>
                        <div className="text-right shrink-0">
                          {s.remaining === 0 ? (
                            <div className="text-[10px] text-amber-300">Full</div>
                          ) : (
                            <div className="text-[10px] text-emerald-300 inline-flex items-center gap-1">
                              <Users className="w-3 h-3" /> {s.remaining} spot{s.remaining === 1 ? "" : "s"} left
                            </div>
                          )}
                          {claimingShift === s.id && <Loader2 className="w-3 h-3 animate-spin text-cyan-300 mt-1 ml-auto" />}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
